import { BetaSchemaForm, ModalFormProps } from '@ant-design/pro-components';
import ProProvider from '@ant-design/pro-provider';
import type {
  FormSchema,
  ProFormColumnsType,
} from '@ant-design/pro-form/es/components/SchemaForm/typing.d.ts';
import { Button, ModalProps, Upload } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { forwardRef, useContext, useImperativeHandle, useRef, useState } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { SelectAddressNoDefaultValue } from '../SelectAddress';
import { ColProps } from 'antd/lib';
import { prepareFormForOpen } from './formState';

// 定义自定义的 valueType 类型
type CustomValueType = keyof typeof valueTypeMap;

// 扩展 ProFormColumnsType 类型
export type ExtendedProFormColumnsType = Omit<ProFormColumnsType, 'valueType'> & {
  valueType?: ProFormColumnsType['valueType'] | CustomValueType;
};

const valueTypeMap = {
  upload: {
    renderFormItem: (text: any, props: any) => {
      const onChange = ({ file, fileList }: any) => {
        // 处理下响应数据给到外面的file对象
        if (file.status === 'done' && file?.response?.data?.url) {
          file.url = file.response.data.url;
          file.osskey = file.response.data?.file_name;
        }
        // 这边是触发下formItem的onChange事件，才能在form那边拿到值
        props.fieldProps.onChange?.(fileList);
      };
      return (
        <Upload {...props.fieldProps} onChange={onChange}>
          <Button icon={<UploadOutlined />}>上传</Button>
        </Upload>
      );
    },
  },
  selectAddress: {
    renderFormItem: (text: any, props: any) => {
      return <SelectAddressNoDefaultValue {...props.fieldProps} />;
    },
  },
};

export type SimpleFormModalProps = Omit<FormSchema, 'columns'> &
  ModalFormProps & { columns: ExtendedProFormColumnsType[] };
type Options = { initValues?: Record<string, any>; resetFields?: boolean; onFinish?: (values: any) => Promise<boolean>,width?: number, labelCol?: ColProps, wrapperCol?: ColProps };
export type SimpleFormModalRef = { open: (mode: Mode, options?: Options) => void };

const modeMap = { Add: { title: '新增' }, Edit: { title: '编辑' } };
type Mode = keyof typeof modeMap |  any;

const SimpleFormModal = forwardRef<SimpleFormModalRef, Omit<SimpleFormModalProps, 'layoutType'>>(
  ({ modalProps: propsModalProps = {}, ...props }, ref) => {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<Mode>('Add');
    const [form] = useForm();
    const optionsCurent = useRef<Options>();

    useImperativeHandle(ref, () => ({
      open: (mode, options = {}) => {
        setOpen(true);
        setMode(mode);
        optionsCurent.current = options;
        prepareFormForOpen(form, options);
      },
    }));

    const modalProps: ModalProps = {
      className: 'BetaSchemaFormModal',
      onClose: () => setOpen(false),
      onCancel: () => setOpen(false),
      maskClosable: false,
      destroyOnClose: true,
      forceRender: true,
      ...propsModalProps,
      width: optionsCurent.current?.width || propsModalProps?.width
    };

    const onFinish = async (values: any) => {
      const flag = await optionsCurent.current?.onFinish?.(values);
      if (flag) {
        setOpen(false);
      }
    };
    const values = useContext(ProProvider);

    return (
      <ProProvider.Provider value={{ ...values, valueTypeMap }}>
        <BetaSchemaForm
          title={modeMap[mode]?.title || mode}
          form={form}
          labelCol={ optionsCurent.current?.labelCol || { span: 8 }}
          wrapperCol={ optionsCurent.current?.labelCol || { span: 10 }}
          layout="horizontal"
          open={open}
          columns={props.columns}
          layoutType="ModalForm"
          onFinish={onFinish}
          modalProps={modalProps}
          {...(props as any)}
        />
      </ProProvider.Provider>
    );
  },
);

export default SimpleFormModal;

// 自定义valueType
// https://github.com/ant-design/pro-components/issues/6949
